import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { from } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { catchAsyncStacktrace } from 'auto-trace';

import { hasAccess } from 'cp-client-auth!sofe';
import { CpNameChip, CpDropdown } from 'canopy-styleguide!sofe';

import { useNav } from '../../../nav.context';
import styles from './nav-profile.styles.css';
import { UserReferralDialogComponent } from '../user-referral.component';
import { fetchEntities } from './nav-profile.resource'
import { NavProfileDefaultContent } from './nav-profile-default-content.component'
import { NavProfileSwitchUser } from './nav-profile-switch-user.component'

export const NavProfile = () => {
  const nav = useNav();

  const [notificationCount, setNotificationCount] = useState(null);
  const [referralDialog, setReferralDialog] = useState(false);
  const [entities, setEntities] = useState()

  // used to show alternate dropdown content
  const [switchingUser, setSwitchingUser] = useState()

  useEffect(() => {
    if (nav.user) {
      const subscription = fetchEntities().subscribe(
        res => {
          const filteredEntities = res.map(i => ({...i, isActive: i.id === nav.user.id}))
            .sort((a, b) => a.company_name.localeCompare(b.company_name))
          setEntities(filteredEntities)
        },
        catchAsyncStacktrace()
      );
      return () => subscription?.unsubscribe();
    }
  }, [nav.user]);


  useEffect(() => {
    const notificationSubscription = from(SystemJS.import('notifications-ui!sofe'))
      .pipe(
        tap(nui => nui.initializeMessageCounts()),
        switchMap(nui => nui.globalUnreadNotifications)
      )
      .subscribe(count => {
        if (count === 0) setNotificationCount();
        else if (count > 99) setNotificationCount('99+');
        else setNotificationCount(count);
      });
    return () => notificationSubscription.unsubscribe();
  }, []);

  const closeDropdown = useCallback(() => {
    if (switchingUser) {
      // delay to prevent a flicker when the dropdown is closed without selecting a user.
      setTimeout(() => setSwitchingUser(false), 200)
    }
  }, [switchingUser])

  const userHasAccess = useMemo(() => hasAccess(nav.user), [nav]);
  const hasAlternateEntities = entities?.length > 1

  return (
    nav &&
    nav.user && (
      <>
        <CpDropdown
          position="right-end"
          appendTo='parent'
          triggerIsBlock
          className={styles.wrapper}
          contentWidth={240}
          contentHeight={switchingUser && 504}
          allowContentClicks
          onClose={closeDropdown}
          onOpen={() => {
            if (nav.secondaryOpen) {
              nav.setSecondaryOpen(null);
            }
          }}
          renderTrigger={({ toggle }) => (
            <a className={styles.wrapper} onClick={toggle}>
              <div className={styles.profileButton}>
                <CpNameChip active name={nav.user.name} imgSrc={nav.user?.profile_picture_url} />
                <div className={styles.notificationWrapper}>
                  {notificationCount ? <div className={styles.notificationCount}>{notificationCount}</div> : null}
                </div>
              </div>
            </a>
          )}
          renderContent={({ close }) => {
            if (hasAlternateEntities && switchingUser) {
              return (
                <NavProfileSwitchUser
                  entities={entities}
                  setSwitchingUser={setSwitchingUser}
                />
              )
            } else return (
              <NavProfileDefaultContent
                setReferralDialog={setReferralDialog}
                notificationCount={notificationCount}
                setSwitchingUser={setSwitchingUser}
                close={close}
                userHasAccess={userHasAccess}
                userId={nav.user.id}
                hasAlternateEntities={hasAlternateEntities}
              />
            )
          }}
        />
        {referralDialog && (
          <UserReferralDialogComponent
            firstName={nav.user.first_name}
            lastName={nav.user.last_name}
            email={nav.user.email}
            directoryId={nav.user.id}
            phoneNumber={nav.user.phone_number}
            onClose={() => setReferralDialog(false)}
          />
        )}
      </>
    )
  );
};
